import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Plus, Clock, Pencil, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { useToast } from '@/hooks/use-toast';

interface LocationTimelineProps {
  locationId: string;
}

export function LocationTimeline({ locationId }: LocationTimelineProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    milestone_date: new Date(),
  });

  const { data: milestones = [], isLoading, isError } = useQuery({
    queryKey: ['charging-location-milestones', locationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('charging_location_milestones')
        .select(`
          *,
          profiles:created_by(display_name, email)
        `)
        .eq('location_id', locationId)
        .order('milestone_date', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    retry: 1,
  });

  const addMutation = useMutation({
    mutationFn: async (milestoneData: any) => {
      const { error } = await supabase
        .from('charging_location_milestones')
        .insert({
          ...milestoneData,
          location_id: locationId,
          created_by: user?.id,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['charging-location-milestones'] });
      toast({ title: 'Mijlpaal toegevoegd' });
      setIsAdding(false);
      setFormData({ title: '', description: '', milestone_date: new Date() });
    },
    onError: (error: any) => {
      console.error('Fout bij toevoegen mijlpaal:', error);
      toast({ 
        title: 'Fout bij opslaan mijlpaal', 
        description: error.message || 'Probeer het opnieuw',
        variant: 'destructive' 
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: any }) => {
      const { error } = await supabase
        .from('charging_location_milestones')
        .update(updates)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['charging-location-milestones'] });
      toast({ title: 'Mijlpaal bijgewerkt' });
      setEditingId(null);
    },
    onError: (error: any) => {
      console.error('Fout bij bijwerken mijlpaal:', error);
      toast({ 
        title: 'Fout bij bijwerken mijlpaal', 
        description: error.message || 'Probeer het opnieuw',
        variant: 'destructive' 
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('charging_location_milestones')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['charging-location-milestones'] });
      toast({ title: 'Mijlpaal verwijderd' });
    },
    onError: (error: any) => {
      console.error('Fout bij verwijderen mijlpaal:', error);
      toast({ 
        title: 'Fout bij verwijderen mijlpaal', 
        description: error.message || 'Probeer het opnieuw',
        variant: 'destructive' 
      });
    },
  });

  const handleAdd = () => {
    if (!formData.title) {
      toast({ title: 'Titel is verplicht', variant: 'destructive' });
      return;
    }
    addMutation.mutate(formData);
  };

  const handleEdit = (milestone: any) => {
    setEditingId(milestone.id);
    setFormData({
      title: milestone.title,
      description: milestone.description || '',
      milestone_date: new Date(milestone.milestone_date),
    });
  };

  const handleUpdate = () => {
    if (!formData.title) {
      toast({ title: 'Titel is verplicht', variant: 'destructive' });
      return;
    }
    updateMutation.mutate({ id: editingId!, updates: formData });
  };

  const handleDelete = (id: string) => {
    if (confirm('Weet je zeker dat je deze mijlpaal wilt verwijderen?')) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Tijdlijn
          </CardTitle>
          <Button size="sm" onClick={() => setIsAdding(!isAdding)}>
            <Plus className="w-4 h-4 mr-1" />
            Mijlpaal
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isAdding && (
          <div className="mb-6 space-y-3 p-4 border rounded-lg">
            <Input
              placeholder="Mijlpaal titel..."
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            />
            <Textarea
              placeholder="Beschrijving..."
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
            />
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {format(formData.milestone_date, 'PPP', { locale: nl })}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={formData.milestone_date}
                  onSelect={(date) => date && setFormData({ ...formData, milestone_date: date })}
                />
              </PopoverContent>
            </Popover>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleAdd} disabled={addMutation.isPending}>
                Opslaan
              </Button>
              <Button size="sm" variant="outline" onClick={() => setIsAdding(false)}>
                Annuleren
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-4">
          {isLoading ? (
            <p className="text-center text-muted-foreground py-4">Laden...</p>
          ) : isError ? (
            <p className="text-center text-destructive py-4">Fout bij laden van mijlpalen</p>
          ) : milestones.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">Geen mijlpalen toegevoegd</p>
          ) : (
            milestones.map((milestone: any) => (
              <div key={milestone.id} className="flex gap-4 border-l-2 border-primary pl-4">
                {editingId === milestone.id ? (
                  <div className="flex-1 space-y-3 p-4 border rounded-lg bg-muted/50">
                    <Input
                      placeholder="Mijlpaal titel..."
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    />
                    <Textarea
                      placeholder="Beschrijving..."
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={2}
                    />
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {format(formData.milestone_date, 'PPP', { locale: nl })}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={formData.milestone_date}
                          onSelect={(date) => date && setFormData({ ...formData, milestone_date: date })}
                        />
                      </PopoverContent>
                    </Popover>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={handleUpdate} disabled={updateMutation.isPending}>
                        Opslaan
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>
                        Annuleren
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="secondary">
                        {format(new Date(milestone.milestone_date), 'dd MMM yyyy', { locale: nl })}
                      </Badge>
                    </div>
                    <h4 className="font-medium">{milestone.title}</h4>
                    {milestone.description && (
                      <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{milestone.description}</p>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      {milestone.profiles && (
                        <p className="text-xs text-muted-foreground">
                          Door: {milestone.profiles.display_name || milestone.profiles.email}
                        </p>
                      )}
                      {(milestone.created_by === user?.id) && (
                        <div className="flex gap-1">
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => handleEdit(milestone)}
                            className="h-7 w-7 p-0"
                          >
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => handleDelete(milestone.id)}
                            className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}
